import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSave, 
  faArrowLeft, 
  faStore, 
  faMapMarkerAlt, 
  faPhone, 
  faBriefcase,
  faUser,
  faCheckCircle,
  faExclamationCircle,
  faShoppingCart,
  faMapPin,
  faStickyNote
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../../AuthContext';
import { userService } from '../../../services/userService';

const EcommerceProfile = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [customId, setCustomId] = useState('');

  const [form, setForm] = useState({
    businessName: '',
    location: '',
    phoneNumber: '',
    businessType: '',
    contactName: '',
    pickupAddress: '',
    senderState: 'Lagos',
    pickupNote: ''
  });

  useEffect(() => {
    if (!currentUser) {
      window.location.href = '/auth';
      return;
    }
    (async () => {
      try {
        const data = await userService.getMe();
        const me = data?.user;
        if (me && me.customId) setCustomId(me.customId);
        let stored = {};
        try { stored = JSON.parse(localStorage.getItem('ecommerceProfile') || '{}'); } catch {}
        setForm({
          businessName: (me && me.businessName) || stored.businessName || '',
          location: (me && me.location) || stored.location || '',
          phoneNumber: (me && me.phone) || stored.phoneNumber || '',
          businessType: (me && me.businessType) || stored.businessType || '',
          contactName: (me && me.contactName) || stored.contactName || '',
          pickupAddress: (me && (me.pickupAddress || me.location)) || stored.pickupAddress || stored.location || '',
          senderState: (me && me.senderState) || stored.senderState || 'Lagos',
          pickupNote: (me && me.pickupNote) || stored.pickupNote || ''
        });
      } catch (_) {}
    })();
  }, [currentUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setError('');
    setSuccess('');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      if (!form.businessName || !form.location || !form.phoneNumber || !form.businessType) {
        setError('Please fill all required fields.');
        setSaving(false);
        return;
      }
      const payload = {
        businessName: form.businessName,
        businessType: form.businessType,
        location: form.location,
        contactName: form.contactName,
        pickupAddress: form.pickupAddress,
        senderState: form.senderState,
        pickupNote: form.pickupNote,
        phoneNumber: form.phoneNumber
      };
      const updated = await userService.updateMe(payload);
      localStorage.setItem('ecommerceProfile', JSON.stringify({
        businessName: updated.businessName || form.businessName,
        location: updated.location || form.location,
        phoneNumber: updated.phone || form.phoneNumber,
        businessType: updated.businessType || form.businessType,
        contactName: updated.contactName || form.contactName,
        pickupAddress: updated.pickupAddress || form.pickupAddress,
        senderState: updated.senderState || form.senderState,
        pickupNote: updated.pickupNote || form.pickupNote
      }));
      setSuccess('Profile saved successfully!');
      navigate('/dashboard/ecommerce');
    } catch (err) {
      setError('Failed to save profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const nigerianStates = [
    'Abia', 'Adamawa', 'Akwa Ibom', 'Anambra', 'Bauchi', 'Bayelsa', 'Benue', 'Borno',
    'Cross River', 'Delta', 'Ebonyi', 'Edo', 'Ekiti', 'Enugu', 'FCT (Abuja)', 'Gombe',
    'Imo', 'Jigawa', 'Kaduna', 'Kano', 'Katsina', 'Kebbi', 'Kogi', 'Kwara', 'Lagos',
    'Nasarawa', 'Niger', 'Ogun', 'Ondo', 'Osun', 'Oyo', 'Plateau', 'Rivers', 'Sokoto',
    'Taraba', 'Yobe', 'Zamfara'
  ];

  return (
    <div className="min-h-screen bg-gray-50 pb-20 md:pb-6">
      {/* Mobile Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => window.location.href = '/dashboard/ecommerce'}
              className="w-10 h-10 flex items-center justify-center rounded-xl bg-gray-100 text-gray-700 hover:bg-gray-200"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
            <h1 className="text-lg font-bold text-gray-900">Business Profile</h1>
            <div className="w-10"></div>
          </div>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Info Banner */}
        <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-2xl p-6 text-white mb-6 shadow-lg">
          <div className="flex items-start">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
              <FontAwesomeIcon icon={faStore} className="text-2xl" />
            </div>
            <div className="flex-1">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <h2 className="text-xl font-bold">Complete Your Business Profile</h2>
                {customId && (
                  <div className="bg-white/20 backdrop-blur-md px-3 py-1 rounded-full border border-white/30 text-xs font-mono tracking-widest">
                    ID: {customId}
                  </div>
                )}
              </div>
              <p className="text-red-50 text-sm">Fill in your business details once and enjoy faster bookings. Your information will be auto-filled in future shipments.</p>
            </div>
          </div>
        </div>

        {/* Alert Messages */}
        {error && (
          <div className="mb-6 bg-red-50 border-l-4 border-red-500 rounded-xl p-4 animate-slide-down">
            <div className="flex items-start">
              <FontAwesomeIcon icon={faExclamationCircle} className="text-red-500 mr-3 mt-0.5 flex-shrink-0" />
              <span className="text-red-700 text-sm">{error}</span>
            </div>
          </div>
        )}

        {success && (
          <div className="mb-6 bg-green-50 border-l-4 border-green-500 rounded-xl p-4 animate-slide-down">
            <div className="flex items-start">
              <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3 mt-0.5 flex-shrink-0" />
              <span className="text-green-700 text-sm">{success}</span>
            </div>
          </div>
        )}

        {/* Form Card */}
        <form onSubmit={handleSave} className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
          <div className="p-6 md:p-8">
            {/* Business Information Section */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center mr-3">
                  <FontAwesomeIcon icon={faShoppingCart} className="text-red-600" />
                </div>
                Business Information
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Business Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <FontAwesomeIcon icon={faStore} className="text-gray-400" />
                    </div>
                    <input
                      type="text"
                      name="businessName"
                      value={form.businessName}
                      onChange={handleChange}
                      className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                      placeholder="e.g., FexCommerce Ltd"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Business Type <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <FontAwesomeIcon icon={faBriefcase} className="text-gray-400" />
                    </div>
                    <select
                      name="businessType"
                      value={form.businessType}
                      onChange={handleChange}
                      className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all appearance-none bg-white"
                      required
                    >
                      <option value="">Select business type</option>
                      <option value="retail">Retail Store</option>
                      <option value="wholesale">Wholesale</option>
                      <option value="dropshipping">Dropshipping</option>
                      <option value="marketplace">Marketplace Seller</option>
                      <option value="manufacturer">Manufacturer</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Contact Person
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <FontAwesomeIcon icon={faUser} className="text-gray-400" />
                    </div>
                    <input
                      type="text"
                      name="contactName"
                      value={form.contactName}
                      onChange={handleChange}
                      className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                      placeholder="e.g., Jane Doe"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <FontAwesomeIcon icon={faPhone} className="text-gray-400" />
                    </div>
                    <input
                      type="tel"
                      name="phoneNumber"
                      value={form.phoneNumber}
                      onChange={handleChange}
                      className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                      placeholder="e.g., 08012345678"
                      required
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Location Section */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="text-blue-600" />
                </div>
                Location & Pickup Details
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Business Location <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <FontAwesomeIcon icon={faMapPin} className="text-gray-400" />
                    </div>
                    <input
                      type="text"
                      name="location"
                      value={form.location}
                      onChange={handleChange}
                      className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                      placeholder="City, Area"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    State
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <FontAwesomeIcon icon={faMapMarkerAlt} className="text-gray-400" />
                    </div>
                    <select
                      name="senderState"
                      value={form.senderState}
                      onChange={handleChange}
                      className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all appearance-none bg-white"
                    >
                      {nigerianStates.map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Default Pickup Address
                  </label>
                  <div className="relative">
                    <div className="absolute top-3 left-0 pl-4 flex items-start pointer-events-none">
                      <FontAwesomeIcon icon={faMapMarkerAlt} className="text-gray-400" />
                    </div>
                    <textarea
                      name="pickupAddress"
                      value={form.pickupAddress}
                      onChange={handleChange}
                      className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all resize-none"
                      placeholder="Full street address where packages will be picked up"
                      rows="3"
                    />
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Pickup Note / Landmark
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <FontAwesomeIcon icon={faStickyNote} className="text-gray-400" />
                    </div>
                    <input
                      type="text"
                      name="pickupNote"
                      value={form.pickupNote}
                      onChange={handleChange}
                      className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                      placeholder="Optional: Nearby landmark or special instructions"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 rounded-xl p-4 mb-6">
              <div className="flex items-start">
                <FontAwesomeIcon icon={faCheckCircle} className="text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-700">
                  <p className="font-semibold mb-1">Why complete your profile?</p>
                  <ul className="list-disc list-inside space-y-1 text-blue-600">
                    <li>Faster booking process - information auto-fills</li>
                    <li>Consistent sender details across all shipments</li>
                    <li>Better tracking and reporting</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="px-6 md:px-8 py-4 bg-gray-50 border-t border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-3">
            <button
              type="button"
              onClick={() => window.location.href = '/dashboard/ecommerce'}
              className="w-full sm:w-auto px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="w-full sm:w-auto px-8 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl font-semibold hover:from-red-700 hover:to-red-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg transform hover:-translate-y-0.5 disabled:transform-none flex items-center justify-center"
            >
              {saving ? (
                <>
                  <svg className="animate-spin h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faSave} className="mr-2" />
                  Save Profile
                </>
              )}
            </button>
          </div>
        </form>
      </main>

      <style jsx>{`
        @keyframes slide-down {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-slide-down {
          animation: slide-down 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default EcommerceProfile;